/**
 * ThreadAddPeoplePanel — the ONE people surface for a thread (group-threading
 * P2c; unified 2026-09-08, chat-thread experience plan PR-1).
 *
 * Two modes over the same roster rows:
 *   - `manage` (the header "people" button, the ⋯ menu): who is in the ACTIVE
 *     thread, add people, and Prest0n's own row with a permission-gated
 *     "Remove" / "Add back". A member without the manage authority sees the
 *     same list, no buttons, and one refusal line — never a button that fails
 *     after the click.
 *   - `compose` (every "+" button): pick who a NEW thread starts with — a
 *     multi-select over the roster, Prest0n on by default with a toggle, and
 *     "Start thread" / "Just me".
 *
 * Purely presentational: the parent (`useThreadAddPeople` / `useThreadCompose`)
 * owns the model, the busy/notice state, and the authorized writes. This
 * component never queries, never writes, and never authorizes — it renders the
 * roster and forwards clicks.
 *
 * Inert by contract: renders `null` unless `enabled && isOpen`.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import type { AddPeopleCandidate, AddPeopleModel } from '../../services/threadAddPeople';
import { disambiguateParticipantLabel } from '../../services/groupThreads/senderIdentity';
import type { ThreadPostRole } from '../../repositories/threadMembershipWriteClient';

// Copy lives in the leaf module so hooks can import it without the panel's
// render dependencies; re-exported here for existing importers.
import { ADD_PEOPLE_COPY } from './threadAddPeopleCopy';
import { BODY_PORTAL_Z_INDEX } from '../../styles/layers';

export { ADD_PEOPLE_COPY };

/** "Start thread with 2 people" — the confirm label for a compose selection. */
export function startThreadLabel(selectedCount: number): string {
  if (selectedCount <= 0) return ADD_PEOPLE_COPY.startThread;
  return `${ADD_PEOPLE_COPY.startThread} with ${selectedCount} ${selectedCount === 1 ? 'person' : 'people'}`;
}

/** Where "Invite a teammate" sends the admin. Kept here so the copy and the destination move together. */
export const TEAM_SETTINGS_PATH = '/settings/team';

/** Pure: case-insensitive filter over label + secondary line. */
export function filterCandidates<T extends { label: string; secondary?: string }>(
  list: readonly T[],
  query: string | undefined,
): T[] {
  const q = (query ?? '').trim().toLowerCase();
  if (!q) return [...list];
  return list.filter(
    (c) => c.label.toLowerCase().includes(q) || (c.secondary ?? '').toLowerCase().includes(q),
  );
}

export type ThreadAddPeoplePanelMode = 'manage' | 'compose';

export interface ThreadAddPeoplePanelProps {
  /** Account-scoped enablement (from the controller). Absent / false ⇒ renders nothing. */
  enabled?: boolean;
  isOpen: boolean;
  loading?: boolean;
  model: AddPeopleModel | null | undefined;
  busyUserId?: string | null;
  notice?: string | null;
  /**
   * The roster READ failed. Renders a retry instead of an empty state — a
   * workspace that couldn't be loaded is not a workspace with nobody in it.
   */
  loadError?: string | null;
  onRetry?: () => void;
  /** Opens Team & Roles so an admin can invite someone without leaving a dead end. */
  onInvite?: () => void;
  /** #21: the per-thread role newly-added people receive; segmented toggle. */
  addRole?: ThreadPostRole;
  onRoleChange?: (role: ThreadPostRole) => void;
  onAdd?: (userId: string) => void;
  onClose?: () => void;
  'data-testid'?: string;

  /** `manage` (default) — the active thread's people; `compose` — a new thread's. */
  mode?: ThreadAddPeoplePanelMode;
  /**
   * manage: may this viewer change who is in the thread? Absent ⇒ true (the
   * pre-2026-09-08 contract). False hides every Add button and the Prest0n
   * control and shows `manageRefusal`.
   */
  canManage?: boolean;
  manageRefusal?: string | null;
  /** manage: is Prest0n in the active thread (registry fact)? Absent ⇒ true. */
  assistantEnabled?: boolean;
  assistantBusy?: boolean;
  /** manage: put Prest0n in / take it out. Absent ⇒ the row has no control. */
  onToggleAssistant?: (next: boolean) => void;
  /** The roster came from a different workspace than the session shows. */
  workspaceMismatchNotice?: string | null;
  /** Search filter over the addable list (both modes). */
  filter?: string;
  onFilterChange?: (value: string) => void;

  /** compose: the picked uids. */
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (userId: string) => void;
  /** compose: Prest0n in the new thread? Absent ⇒ true. */
  composeAssistantEnabled?: boolean;
  onToggleComposeAssistant?: (next: boolean) => void;
  onConfirm?: () => void;
  onJustMe?: () => void;
  confirmBusy?: boolean;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.32)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // The chat's own portal rung — see MessageSenderChip (2026-09-11).
  zIndex: BODY_PORTAL_Z_INDEX,
};

const cardStyle: React.CSSProperties = {
  background: 'var(--surface, #fff)',
  color: 'var(--text, #16181d)',
  width: 'min(440px, calc(100vw - 24px))',
  maxHeight: 'min(80vh, 640px)',
  borderRadius: 14,
  boxShadow: '0 12px 40px rgba(0,0,0,0.28)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 16px',
  borderBottom: '1px solid rgba(127,127,127,0.18)',
  fontWeight: 600,
};

const listStyle: React.CSSProperties = { overflowY: 'auto', padding: '8px 0', flex: 1 };

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '8px 16px',
};

const labelWrapStyle: React.CSSProperties = { minWidth: 0, display: 'flex', flexDirection: 'column' };
const labelStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: 14,
};
const secondaryStyle: React.CSSProperties = { fontSize: 12, opacity: 0.62 };

const addBtnStyle: React.CSSProperties = {
  flex: '0 0 auto',
  border: '1px solid rgba(127,127,127,0.35)',
  background: 'transparent',
  color: 'inherit',
  borderRadius: 8,
  padding: '4px 12px',
  fontSize: 13,
  cursor: 'pointer',
};

const primaryBtnStyle: React.CSSProperties = {
  ...addBtnStyle,
  border: '1px solid rgba(198,167,94,0.7)',
  background: 'rgba(198,167,94,0.18)',
  color: '#8a6d2f',
  fontWeight: 600,
  padding: '8px 14px',
};

const checkStyle = (checked: boolean): React.CSSProperties => ({
  flex: '0 0 auto',
  width: 22,
  height: 22,
  borderRadius: 6,
  border: checked ? '1px solid rgba(198,167,94,0.9)' : '1px solid rgba(127,127,127,0.45)',
  background: checked ? 'rgba(198,167,94,0.22)' : 'transparent',
  color: '#8a6d2f',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  cursor: 'pointer',
});

const noticeStyle: React.CSSProperties = {
  margin: '10px 16px 0',
  padding: '8px 10px',
  borderRadius: 8,
  background: 'rgba(220,72,72,0.12)',
  color: '#c0392b',
  fontSize: 13,
};

const infoNoticeStyle: React.CSSProperties = {
  ...noticeStyle,
  background: 'rgba(198,167,94,0.14)',
  color: '#8a6d2f',
};

const sectionLabelStyle: React.CSSProperties = {
  padding: '10px 16px 4px',
  fontSize: 11,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  opacity: 0.55,
};

const emptyStyle: React.CSSProperties = { padding: '18px 16px', fontSize: 14, opacity: 0.72 };

const emptyHintStyle: React.CSSProperties = {
  padding: '0 16px 4px',
  fontSize: 13,
  opacity: 0.6,
  lineHeight: 1.4,
};

const linkBtnStyle: React.CSSProperties = {
  margin: '8px 16px 4px',
  border: '1px solid rgba(198,167,94,0.55)',
  background: 'rgba(198,167,94,0.14)',
  color: '#8a6d2f',
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const searchStyle: React.CSSProperties = {
  margin: '10px 16px 0',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid rgba(127,127,127,0.3)',
  background: 'transparent',
  color: 'inherit',
  fontSize: 14,
  width: 'calc(100% - 32px)',
  boxSizing: 'border-box',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
  padding: '12px 16px',
  borderTop: '1px solid rgba(127,127,127,0.18)',
};

/** Muted, non-actionable row: someone who exists but can't be added right now. */
const InfoRow: React.FC<{ label: string; secondary?: string; note?: string; testAttr?: Record<string, string> }> = ({
  label,
  secondary,
  note,
  testAttr,
}) => (
  <div style={{ ...rowStyle, opacity: 0.6 }} {...(testAttr ?? {})}>
    <span style={labelWrapStyle}>
      <span style={labelStyle} title={label}>
        {label}
      </span>
      {secondary && <span style={secondaryStyle}>{secondary}</span>}
      {note && <span style={secondaryStyle}>{note}</span>}
    </span>
  </div>
);

const CandidateRow: React.FC<{
  candidate: AddPeopleCandidate;
  busy: boolean;
  anyBusy: boolean;
  onAdd?: (userId: string) => void;
}> = ({ candidate, busy, anyBusy, onAdd }) => {
  /**
   * A HUMAN account whose display name collides with the assistant (the
   * production case: a real teammate account on prest0n@prest0.ai) must not
   * render as "Prest0n" next to an Add button — the owner read exactly that
   * row as the assistant being addable (2026-08-07). Same W2 rule the
   * transcript chips use: never present a human under the assistant's name.
   */
  const label = disambiguateParticipantLabel(
    candidate.label,
    { email: candidate.secondary },
    candidate.userId,
  );
  return (
  <div style={rowStyle} data-add-people-row={candidate.userId}>
    <span style={labelWrapStyle}>
      <span style={labelStyle} title={label}>
        {label}
      </span>
      {candidate.secondary && <span style={secondaryStyle}>{candidate.secondary}</span>}
    </span>
    <button
      type="button"
      style={{ ...addBtnStyle, opacity: anyBusy && !busy ? 0.5 : 1 }}
      disabled={anyBusy}
      data-add-people-add={candidate.userId}
      onClick={() => onAdd?.(candidate.userId)}
    >
      {busy ? ADD_PEOPLE_COPY.adding : ADD_PEOPLE_COPY.add}
    </button>
  </div>
  );
};

/** compose: one selectable person. */
const SelectRow: React.FC<{
  candidate: AddPeopleCandidate;
  selected: boolean;
  disabled: boolean;
  onToggle?: (userId: string) => void;
}> = ({ candidate, selected, disabled, onToggle }) => {
  const label = disambiguateParticipantLabel(candidate.label, { email: candidate.secondary }, candidate.userId);
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      disabled={disabled}
      style={{ ...rowStyle, width: '100%', background: 'transparent', border: 'none', color: 'inherit', textAlign: 'left', cursor: disabled ? 'default' : 'pointer' }}
      data-add-people-row={candidate.userId}
      data-add-people-select={candidate.userId}
      data-selected={selected ? 'true' : 'false'}
      onClick={() => onToggle?.(candidate.userId)}
    >
      <span style={labelWrapStyle}>
        <span style={labelStyle} title={label}>{label}</span>
        {candidate.secondary && <span style={secondaryStyle}>{candidate.secondary}</span>}
      </span>
      <span aria-hidden="true" style={checkStyle(selected)}>{selected ? '✓' : ''}</span>
    </button>
  );
};

export const ThreadAddPeoplePanel: React.FC<ThreadAddPeoplePanelProps> = ({
  enabled,
  isOpen,
  loading,
  model,
  busyUserId,
  notice,
  loadError,
  onRetry,
  onInvite,
  addRole,
  onRoleChange,
  onAdd,
  onClose,
  mode = 'manage',
  canManage,
  manageRefusal,
  assistantEnabled,
  assistantBusy,
  onToggleAssistant,
  workspaceMismatchNotice,
  filter,
  onFilterChange,
  selectedIds,
  onToggleSelect,
  composeAssistantEnabled,
  onToggleComposeAssistant,
  onConfirm,
  onJustMe,
  confirmBusy,
  ...rest
}) => {
  if (!enabled || !isOpen) return null;

  const compose = mode === 'compose';
  const mayManage = compose ? true : canManage !== false;
  const anyBusy = !!busyUserId || !!confirmBusy;
  const addableAll = model?.addable ?? [];
  const addable = filterCandidates(addableAll, filter);
  const existing = model?.existing ?? [];
  const unavailable = model?.unavailable ?? [];
  const pending = model?.pending ?? [];
  const failed = !!loadError;
  const selected = selectedIds ?? new Set<string>();
  const assistantIn = compose ? composeAssistantEnabled !== false : assistantEnabled !== false;

  // Four distinct outcomes, four distinct answers. Collapsing the first into the
  // others is what produced "No one else in this workspace to add." on a
  // workspace that demonstrably had someone else in it.
  const showEmpty = !loading && !failed && addableAll.length === 0;
  const emptyMsg =
    existing.length > 0
      ? ADD_PEOPLE_COPY.emptyAllIn
      : unavailable.length > 0 || pending.length > 0
        ? ''
        : ADD_PEOPLE_COPY.emptyNoRoster;
  // The dead end this dialog used to be: nobody to add, and nothing to do about
  // it. Offer the invite path whenever there is genuinely no one else yet.
  const showInvite =
    showEmpty && existing.length === 0 && unavailable.length === 0 && pending.length === 0 && !!onInvite;
  const role: ThreadPostRole = addRole === 'viewer' ? 'viewer' : 'contributor';
  const roleTabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '6px 10px',
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    color: active ? '#8a6d2f' : 'inherit',
    background: active ? 'rgba(198,167,94,0.16)' : 'transparent',
    border: 'none',
    borderRadius: 7,
    cursor: anyBusy ? 'default' : 'pointer',
  });

  const content = (
    <div
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-label={compose ? ADD_PEOPLE_COPY.composeTitle : ADD_PEOPLE_COPY.title}
      // Marks this as a body-portaled chat surface so the chat's document-level
      // click-outside handlers don't treat interactions here as "outside the chat"
      // (which would close the whole pane). Mirrors ConfirmationModal.
      data-prest0n-portal="true"
      data-testid={rest['data-testid']}
      data-add-people-mode={mode}
      onClick={onClose}
    >
      <div style={cardStyle} data-add-people-panel="" onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <span>{compose ? ADD_PEOPLE_COPY.composeTitle : ADD_PEOPLE_COPY.title}</span>
          <button
            type="button"
            style={{ ...addBtnStyle, border: 'none' }}
            aria-label={ADD_PEOPLE_COPY.close}
            data-add-people-close=""
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {compose && <div style={emptyHintStyle}>{ADD_PEOPLE_COPY.composeHint}</div>}

        {notice && (
          <div style={noticeStyle} role="alert" data-add-people-notice="">
            {notice}
          </div>
        )}

        {/* The viewer may look but not change (2026-09-08): one honest line
            instead of buttons the server would refuse after the click. */}
        {!compose && !mayManage && (
          <div style={infoNoticeStyle} data-add-people-refusal="">
            {manageRefusal || ADD_PEOPLE_COPY.manageRefusal}
          </div>
        )}

        {workspaceMismatchNotice && (
          <div style={infoNoticeStyle} data-add-people-mismatch="">
            {workspaceMismatchNotice}
          </div>
        )}

        {/* #21: per-thread access for the next person added. Thread-scoped and
            independent of workspace roles. Hidden while the roster is unreadable —
            picking a role for a list we can't show is noise — and for a viewer
            who cannot add anyone. */}
        {!failed && mayManage && (
        <div style={{ padding: '10px 16px 2px' }} data-add-people-role={role}>
          <div
            style={{
              display: 'flex',
              gap: 4,
              background: 'rgba(127,127,127,0.10)',
              borderRadius: 9,
              padding: 3,
            }}
            role="tablist"
            aria-label="Access for added people"
          >
            <button
              type="button"
              role="tab"
              aria-selected={role === 'contributor'}
              disabled={anyBusy}
              style={roleTabStyle(role === 'contributor')}
              data-add-people-role-contributor=""
              onClick={() => onRoleChange?.('contributor')}
            >
              {ADD_PEOPLE_COPY.roleContributor}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={role === 'viewer'}
              disabled={anyBusy}
              style={roleTabStyle(role === 'viewer')}
              data-add-people-role-viewer=""
              onClick={() => onRoleChange?.('viewer')}
            >
              {ADD_PEOPLE_COPY.roleViewer}
            </button>
          </div>
          <div style={{ ...secondaryStyle, padding: '5px 2px 0' }}>
            {role === 'viewer' ? ADD_PEOPLE_COPY.roleHintViewer : ADD_PEOPLE_COPY.roleHintContributor}
          </div>
        </div>
        )}

        {/* Search (2026-09-08): a long roster is found, not scrolled. */}
        {!failed && onFilterChange && (
          <input
            type="search"
            value={filter ?? ''}
            placeholder={ADD_PEOPLE_COPY.searchPlaceholder}
            aria-label={ADD_PEOPLE_COPY.searchPlaceholder}
            style={searchStyle}
            data-add-people-search=""
            onChange={(e) => onFilterChange(e.target.value)}
          />
        )}

        <div style={listStyle}>
          {/*
            Prest0n is a DEFAULT member of every thread (owner directive
            2026-08-07) — never an addable candidate from the roster (its own
            member doc is dropped by the normalizer; see isPlatformAgentIdentity).
            Since 2026-09-08 a permitted user may take Prest0n OUT of a thread
            and put it back; the row states the fact and carries that one
            control. The tabs above pick the role for the NEXT human added; the
            assistant's participation is not role-scoped. Rendered above
            loading/failure states because it is state, not fetched data.
          */}
          {!failed && (
            <div style={rowStyle} data-add-people-assistant="" data-assistant-enabled={assistantIn ? 'true' : 'false'}>
              <span style={labelWrapStyle}>
                <span style={labelStyle}>{ADD_PEOPLE_COPY.assistantName}</span>
                <span style={secondaryStyle}>
                  {compose
                    ? (assistantIn ? ADD_PEOPLE_COPY.assistantComposeOn : ADD_PEOPLE_COPY.assistantComposeOff)
                    : (assistantIn ? ADD_PEOPLE_COPY.assistantAlwaysHere : ADD_PEOPLE_COPY.assistantRemoved)}
                </span>
              </span>
              {compose ? (
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={assistantIn}
                  aria-label="Prest0n in this thread"
                  disabled={anyBusy}
                  style={checkStyle(assistantIn)}
                  data-add-people-assistant-toggle=""
                  onClick={() => onToggleComposeAssistant?.(!assistantIn)}
                >
                  {assistantIn ? '✓' : ''}
                </button>
              ) : (
                mayManage && onToggleAssistant && (
                  <button
                    type="button"
                    style={{ ...addBtnStyle, opacity: anyBusy || assistantBusy ? 0.5 : 1 }}
                    disabled={anyBusy || !!assistantBusy}
                    data-add-people-assistant-toggle=""
                    aria-label={assistantIn ? 'remove Prest0n from this thread' : 'add Prest0n back to this thread'}
                    onClick={() => onToggleAssistant(!assistantIn)}
                  >
                    {assistantBusy
                      ? (assistantIn ? ADD_PEOPLE_COPY.removingAssistant : ADD_PEOPLE_COPY.addingAssistant)
                      : (assistantIn ? ADD_PEOPLE_COPY.removeAssistant : ADD_PEOPLE_COPY.addAssistantBack)}
                  </button>
                )
              )}
            </div>
          )}
          {/* Instant-open (2026-08-09): the loading line shows only when there
              is genuinely nothing to render yet. A reopen (state kept) or a
              seeded roster paints immediately; load() is silent background
              revalidation that must never blank rows the user can see. */}
          {loading && addableAll.length === 0 && existing.length === 0 && (
            <div style={emptyStyle}>{ADD_PEOPLE_COPY.loading}</div>
          )}

          {/* A read that FAILED. Never an empty state — the workspace may well be
              full of people we simply couldn't fetch. */}
          {!loading && failed && (
            <div data-add-people-load-error="">
              <div style={emptyStyle}>{loadError}</div>
              {onRetry && (
                <button type="button" style={linkBtnStyle} data-add-people-retry="" onClick={onRetry}>
                  {ADD_PEOPLE_COPY.retry}
                </button>
              )}
            </div>
          )}

          {!failed && compose &&
            addable.map((c) => (
              <SelectRow
                key={c.userId}
                candidate={c}
                selected={selected.has(c.userId)}
                disabled={anyBusy}
                onToggle={onToggleSelect}
              />
            ))}

          {!failed && !compose && mayManage &&
            addable.map((c) => (
              <CandidateRow
                key={c.userId}
                candidate={c}
                busy={busyUserId === c.userId}
                anyBusy={anyBusy}
                onAdd={onAdd}
              />
            ))}

          {!failed && !compose && !mayManage && addable.length > 0 && (
            <>
              <div style={sectionLabelStyle}>People in this workspace</div>
              {addable.map((c) => (
                <InfoRow
                  key={c.userId}
                  label={disambiguateParticipantLabel(c.label, { email: c.secondary }, c.userId)}
                  secondary={c.secondary}
                  testAttr={{ 'data-add-people-readonly': c.userId }}
                />
              ))}
            </>
          )}

          {!failed && !loading && addableAll.length > 0 && addable.length === 0 && (
            <div style={emptyStyle} data-add-people-no-matches="">{ADD_PEOPLE_COPY.noMatches}</div>
          )}

          {showEmpty && emptyMsg && (
            <div style={emptyStyle} data-add-people-empty="">
              {emptyMsg}
            </div>
          )}

          {showInvite && (
            <>
              <div style={emptyHintStyle}>{ADD_PEOPLE_COPY.emptyNoRosterHint}</div>
              <button type="button" style={linkBtnStyle} data-add-people-invite="" onClick={onInvite}>
                {ADD_PEOPLE_COPY.manageTeam}
              </button>
            </>
          )}

          {!failed && existing.length > 0 && (
            <>
              <div style={sectionLabelStyle}>{ADD_PEOPLE_COPY.alreadyHere}</div>
              {existing.map((c) => (
                <InfoRow
                  key={c.userId}
                  label={disambiguateParticipantLabel(c.label, { email: c.secondary }, c.userId)}
                  secondary={c.secondary}
                  testAttr={{ 'data-add-people-existing': c.userId }}
                />
              ))}
            </>
          )}

          {/* Members the workspace exists but won't admit yet — shown WITH the
              reason instead of being silently filtered out of the picker. */}
          {!failed && unavailable.length > 0 && (
            <>
              <div style={sectionLabelStyle}>{ADD_PEOPLE_COPY.notYetAvailable}</div>
              {unavailable.map((c) => (
                <InfoRow
                  key={c.userId}
                  label={c.label}
                  secondary={c.secondary}
                  note={c.blockedReason}
                  testAttr={{ 'data-add-people-unavailable': c.userId }}
                />
              ))}
            </>
          )}

          {/* "I invited them — where are they?" answered in place, so nobody has
              to guess whether the invite worked. */}
          {!failed && pending.length > 0 && (
            <>
              <div style={sectionLabelStyle}>{ADD_PEOPLE_COPY.invited}</div>
              {pending.map((p) => (
                <InfoRow
                  key={p.inviteId}
                  label={p.displayName || p.email}
                  secondary={p.displayName ? p.email : undefined}
                  note={
                    p.status === 'expired'
                      ? ADD_PEOPLE_COPY.inviteExpired
                      : ADD_PEOPLE_COPY.invitePending
                  }
                  testAttr={{ 'data-add-people-pending': p.inviteId }}
                />
              ))}
              <div style={emptyHintStyle}>{ADD_PEOPLE_COPY.invitedHint}</div>
            </>
          )}
        </div>

        {compose && (
          <div style={footerStyle} data-add-people-compose-footer="">
            <button
              type="button"
              style={addBtnStyle}
              disabled={anyBusy}
              data-add-people-just-me=""
              onClick={onJustMe}
            >
              {ADD_PEOPLE_COPY.justMe}
            </button>
            <button
              type="button"
              style={{ ...primaryBtnStyle, opacity: anyBusy ? 0.6 : 1 }}
              disabled={anyBusy}
              data-add-people-confirm=""
              onClick={onConfirm}
            >
              {confirmBusy ? ADD_PEOPLE_COPY.starting : startThreadLabel(selected.size)}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Render into document.body so the fixed overlay escapes the docked chat
  // widget's transformed stacking context (which was trapping position:fixed and
  // letting thread names / chips bleed over the modal). SSR/test-safe: fall back to
  // inline render when there is no document (node unit env uses renderToStaticMarkup).
  return typeof document !== 'undefined' && document.body
    ? ReactDOM.createPortal(content, document.body)
    : content;
};

export default ThreadAddPeoplePanel;
